import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";

export interface OvernightStatus {
  done: number;
  total: number;
  status: "idle" | "running" | "complete" | "error";
  /**
   * Teamnummern, für die gebaut wird, und die schon fertigen. Damit lässt sich
   * je Prototyp ein Kästchen zeigen, statt nur „3 von 6" — und es sind die
   * Prompts der laufenden Besuchergruppe, nicht alle Teams der Tabelle.
   * Ältere Deployments liefern die Felder nicht.
   */
  teams: number[];
  fertig: number[];
  /**
   * Geschriebene Zeichen je noch laufendem Prototyp. Die Generierungs-Lambda
   * meldet sie während des Streams; damit lässt sich ein echter Balken zeichnen
   * statt einer Animation, die Fortschritt nur behauptet.
   */
  fortschritt: Record<number, number>;
}

/**
 * Pollt den Fortschritt des Overnight-Lambdas (wie viele Outputs stehen schon).
 *
 * Einziger Poll-Mechanismus dafür im Frontend — Sidebar und AR-Sitzungs-
 * steuerung teilen ihn sich, statt zwei Intervalle nebeneinander laufen zu
 * lassen. `active=false` schaltet ab und leert den Wert.
 */
export function useOvernightStatus(
  active: boolean,
  intervalMs = 2000,
): OvernightStatus | null {
  const { state } = useStore();
  const token = state.auth.token;
  const [status, setStatus] = useState<OvernightStatus | null>(null);

  useEffect(() => {
    if (!active) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.overnight.status(token);
        if (cancelled) return;
        setStatus({
          done: r.done,
          total: r.total,
          status: r.status,
          teams: r.teams ?? [],
          fertig: r.fertig ?? [],
          fortschritt: r.fortschritt ?? {},
        });
      } catch {
        /* Zwischenfehler beim Pollen sind erwartbar — letzter Wert bleibt. */
      }
    };
    load();
    const id = setInterval(load, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [active, token, intervalMs]);

  return status;
}
